import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Select,
  Input,
  Checkbox,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Alert,
  AlertIcon,
  Badge,
  Textarea,
  InputGroup,
  InputLeftAddon,
  Collapse,
  Button,
  Box,
  Divider,
} from '@chakra-ui/react';
import { FiDollarSign, FiCreditCard, FiTrendingUp, FiFileText, FiShield } from 'react-icons/fi';
import { RegistrationData } from '../index';

interface BankingPaymentProps {
  data: RegistrationData;
  updateData: (data: RegistrationData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const BankingPayment: React.FC<BankingPaymentProps> = ({
  data,
  updateData,
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const progressSuccessBg = useColorModeValue('green.50', 'green.900');
  const [customDepositAmount, setCustomDepositAmount] = useState('');

  const paymentTermsOptions = [
    { value: '1_15_net_30', label: '1% 15 Net 30 (Standard)' },
    { value: 'net_15', label: 'Net 15' },
    { value: 'net_30', label: 'Net 30 (Minimum Acceptable)' },
    { value: '2_10_net_30', label: '2% 10 Net 30' },
    { value: 'due_on_receipt', label: 'Due on Receipt' },
    { value: 'custom', label: 'Custom Terms' },
  ];

  const pricingApproaches = [
    { value: 'list_minus_discount', label: 'List Price Minus Discount' },
    { value: 'net_pricing', label: 'Net Pricing' },
    { value: 'cost_plus', label: 'Cost Plus Markup' },
    { value: 'competitive_bid', label: 'Competitive Bidding' },
    { value: 'tiered_pricing', label: 'Tiered Volume Pricing' },
    { value: 'contract_pricing', label: 'Contract Pricing' },
  ];

  const depositOptions = [
    { value: '25', label: '25%' },
    { value: '30', label: '30%' },
    { value: '50', label: '50%' },
    { value: 'other', label: 'Other (specify amount)' },
  ];

  const updatePaymentInfo = (field: string, value: any) => {
    updateData({
      ...data,
      paymentInfo: {
        ...data.paymentInfo,
        [field]: value,
      },
    });
  };

  const updateBankingInfo = (field: string, value: string) => {
    updateData({
      ...data,
      banking: {
        ...data.banking,
        [field]: value,
      },
    });
  };

  const handleDepositRequiredChange = (checked: boolean) => {
    updatePaymentInfo('depositRequired', checked);
    if (!checked) {
      updatePaymentInfo('depositDetails', '');
      setCustomDepositAmount('');
    }
  };

  const handleDepositAmountChange = (value: string) => {
    if (value === 'other') {
      updatePaymentInfo('depositDetails', 'other');
    } else {
      updatePaymentInfo('depositDetails', `${value}%`);
      setCustomDepositAmount('');
    }
  };

  const handleCustomDepositChange = (value: string) => {
    setCustomDepositAmount(value);
    updatePaymentInfo('depositDetails', `custom: ${value}`);
  };

  const isFormValid = () => {
    return data.paymentInfo?.paymentTerms && 
           data.paymentInfo?.pricingApproach &&
           data.banking?.accountName &&
           data.banking?.bankName &&
           data.banking?.accountNumber &&
           data.banking?.routingNumber;
  };

  return (
    <VStack spacing={8} align="stretch">
      {/* Payment Terms */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiDollarSign} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Payment Terms
              </Text>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl isRequired>
                <FormLabel>Payment Terms</FormLabel>
                <Select
                  placeholder="Select payment terms"
                  value={data.paymentInfo?.paymentTerms || ''}
                  onChange={(e) => updatePaymentInfo('paymentTerms', e.target.value)}
                >
                  {paymentTermsOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Pricing Approach</FormLabel>
                <Select
                  placeholder="Select pricing method"
                  value={data.paymentInfo?.pricingApproach || ''}
                  onChange={(e) => updatePaymentInfo('pricingApproach', e.target.value)}
                >
                  {pricingApproaches.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>

            {/* Custom Terms */}
            {data.paymentInfo?.paymentTerms === 'custom' && (
              <FormControl>
                <FormLabel>Custom Payment Terms</FormLabel>
                <Textarea
                  placeholder="Describe your custom payment terms..."
                  value={data.paymentInfo?.customPaymentTerms || ''}
                  onChange={(e) => updatePaymentInfo('customPaymentTerms', e.target.value)}
                  rows={3}
                />
              </FormControl>
            )}

            {/* Pricing Details */}
            {data.paymentInfo?.pricingApproach && (
              <FormControl>
                <FormLabel>Pricing Details <Text as="span" color="gray.500" fontSize="sm">(optional)</Text></FormLabel>
                <Textarea
                  placeholder="Additional pricing information, discount structures, volume pricing, etc."
                  value={data.paymentInfo?.pricingDetails || ''}
                  onChange={(e) => updatePaymentInfo('pricingDetails', e.target.value)}
                  rows={3}
                />
              </FormControl>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Order Management */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiFileText} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Order Management
              </Text>
            </HStack>

            <VStack spacing={6} align="stretch">
              {/* Deposit Requirements */}
              <Box>
                <FormControl>
                  <Checkbox
                    isChecked={data.paymentInfo?.depositRequired || false}
                    onChange={(e) => handleDepositRequiredChange(e.target.checked)}
                  >
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="medium">
                        Require deposits for orders
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Check if you require customer deposits before order fulfillment
                      </Text>
                    </VStack>
                  </Checkbox>
                </FormControl>

                <Collapse in={data.paymentInfo?.depositRequired || false} animateOpacity>
                  <Box bg={sectionBg} p={6} borderRadius="md" mt={4}>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel>Deposit Amount</FormLabel>
                        <Select
                          placeholder="Select deposit percentage"
                          value={
                            data.paymentInfo?.depositDetails?.includes('custom:') 
                              ? 'other' 
                              : data.paymentInfo?.depositDetails?.replace('%', '') || ''
                          }
                          onChange={(e) => handleDepositAmountChange(e.target.value)}
                        >
                          {depositOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Custom Deposit Amount */}
                      <Collapse 
                        in={data.paymentInfo?.depositDetails === 'other' || data.paymentInfo?.depositDetails?.includes('custom:')} 
                        animateOpacity
                      >
                        <FormControl>
                          <FormLabel>Custom Deposit Amount</FormLabel>
                          <Input
                            placeholder="e.g., 15%, $500, 20% of order total"
                            value={customDepositAmount || (data.paymentInfo?.depositDetails?.includes('custom:') ? data.paymentInfo.depositDetails.replace('custom: ', '') : '')}
                            onChange={(e) => handleCustomDepositChange(e.target.value)}
                          />
                          <Text fontSize="sm" color="gray.600" mt={1}>
                            Specify the exact deposit amount, percentage, or calculation method
                          </Text>
                        </FormControl>
                      </Collapse>
                    </VStack>
                  </Box>
                </Collapse>
              </Box>

              <Divider />

              {/* Order Acknowledgements */}
              <Box>
                <FormControl>
                  <Checkbox
                    isChecked={data.paymentInfo?.acknowledgements || false}
                    onChange={(e) => updatePaymentInfo('acknowledgements', e.target.checked)}
                  >
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="medium">
                        Send order acknowledgements
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Confirm orders with customers after receipt
                      </Text>
                    </VStack>
                  </Checkbox>
                </FormControl>

                {data.paymentInfo?.acknowledgements && (
                  <Box bg={sectionBg} p={6} borderRadius="md" mt={4}>
                    <FormControl>
                      <FormLabel>Acknowledgement Process</FormLabel>
                      <Textarea
                        placeholder="Describe your order acknowledgement process, timeline, and any specific requirements..."
                        value={data.paymentInfo?.acknowledgementProcess || ''}
                        onChange={(e) => updatePaymentInfo('acknowledgementProcess', e.target.value)}
                        rows={3}
                      />
                    </FormControl>
                  </Box>
                )}
              </Box>
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Banking Information */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiShield} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Banking Information
              </Text>
            </HStack>

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" fontWeight="medium">
                  Secure Banking Setup
                </Text>
                <Text fontSize="sm">
                  This information is encrypted and used only for payment processing. Account details will be verified before activation.
                </Text>
              </VStack>
            </Alert>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl isRequired>
                <FormLabel>Account Holder Name</FormLabel>
                <InputGroup>
                  <InputLeftAddon>
                    <Icon as={FiCreditCard} />
                  </InputLeftAddon>
                  <Input
                    placeholder="Account holder name"
                    value={data.banking?.accountName || ''}
                    onChange={(e) => updateBankingInfo('accountName', e.target.value)}
                  />
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Bank Name</FormLabel>
                <Input
                  placeholder="Financial institution name"
                  value={data.banking?.bankName || ''}
                  onChange={(e) => updateBankingInfo('bankName', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Account Number</FormLabel>
                <Input
                  type="password"
                  placeholder="••••••••••••"
                  value={data.banking?.accountNumber || ''}
                  onChange={(e) => updateBankingInfo('accountNumber', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Routing Number</FormLabel>
                <Input
                  placeholder="9-digit routing number"
                  value={data.banking?.routingNumber || ''}
                  onChange={(e) => updateBankingInfo('routingNumber', e.target.value)}
                />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>Additional Wire Transfer Info <Text as="span" color="gray.500" fontSize="sm">(optional)</Text></FormLabel>
              <Textarea
                placeholder="Any additional wire transfer details, SWIFT codes, or special instructions..."
                rows={3}
              />
              <Text fontSize="sm" color="gray.600" mt={1}>
                Only needed if you require wire transfers for payments
              </Text>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Progress Indicator */}
      {isFormValid() && (
        <Card bg={progressSuccessBg} borderColor="green.200">
          <CardBody>
            <HStack spacing={3}>
              <Icon as={FiDollarSign} color="green.500" />
              <Text fontSize="sm" color="green.700" fontWeight="medium">
                Payment and banking information completed
              </Text>
              <Badge colorScheme="green" ml="auto">
                Ready to continue
              </Badge>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default BankingPayment; 